<script lang="ts" module>
  import type { AISuggestionPromptId } from '@/teoCore/text/AIHandler/aiSuggestionPresets';
  import CtaButton from '@/ui/elements/buttons/CTAButton.svelte';
  import BaseCard from '@/ui/elements/card/BaseCard.svelte';
  import CardHeader, { type CardHeaderProps } from '@/ui/elements/card/CardHeader.svelte';
  import InfoCard from '@/ui/elements/card/InfoCard.svelte';

  import AiContainer, { type AIContainerProps } from './cmps/AIContainer.svelte';
  import type { AIStyleProps } from './cmps/AIStyle.svelte';

  export type AICardState = 'default' | 'loading' | 'result' | 'noOptimization';

  export interface AdditionalProps {
    onLoadClick?: () => void;
    onUnloadClick?: () => void;
    onInsertClick?: AIContainerProps['onInsertClick'];
    onSuggestionShown?: AIContainerProps['onSuggestionShown'];
    onPromptSelect?: (item: AISuggestionPromptId) => void;
    state: AICardState;
    cardLoadLabel?: string;
    /** Blocks the button that starts a request, for when the card has nothing to ask for. */
    loadDisabled?: boolean;
    promptItems?: AIContainerProps['aiStyleItems'];
    promptItemSelected?: string;
    aiSuggestions?: AIContainerProps['aiSuggestions'];
    aiDescription?: AIContainerProps['aiDescription'];
    noOptimizationText?: string;
    instructionText?: string;
    headerAction?: AICardHeaderActionProps;
    onLoadSuggestion: (modifier: string) => void;
    onRemoveSuggestion?: AIContainerProps['onRemoveSuggestion'];
  }

  export type AICardProps = AdditionalProps & Pick<CardHeaderProps, 'cardLabel'> & AIStyleProps;
</script>

<script lang="ts">
  import AICardHeaderAction, {
    type AICardHeaderActionProps,
  } from './cmps/AICardHeaderAction.svelte';

  let { cardLoadLabel: cardLoadLabelProp = 'Load', headerAction, ...props }: AICardProps = $props();
  let stateProp: AICardState = $derived(props.state);

  let headerActionProps: AICardHeaderActionProps = $derived(
    headerAction ?? {
      type: 'button',
      onclick: () => props.onLoadClick?.(),
      icon: 'MagicButton',
      variant: 'primary',
      disabled: props.loadDisabled,
      loading: stateProp === 'loading',
      label: cardLoadLabelProp,
    },
  );
</script>

<BaseCard hideLine={true}>
  {#snippet header()}
    <CardHeader cardLabel={props.cardLabel}>
      {#if stateProp === 'default'}
        <AICardHeaderAction {...headerActionProps} />
      {:else}
        <CtaButton
          variant="secondary"
          icon="Undo"
          tooltip="KI-Optimierung beenden"
          disabled={stateProp === 'loading'}
          onclick={() => {
            props.onUnloadClick?.();
          }}
        ></CtaButton>
      {/if}
    </CardHeader>
  {/snippet}

  {#if stateProp === 'result' || stateProp === 'loading'}
    <AiContainer
      aiDescription={props.aiDescription}
      aiSuggestions={props.aiSuggestions ?? []}
      aiStyleItems={props.aiStyleItems}
      aiStyleLabel={props.aiStyleLabel}
      aiStyleTooltip={props.aiStyleTooltip}
      aiStyleDisabled={props.aiStyleDisabled}
      onInsertClick={props.onInsertClick}
      onSuggestionShown={props.onSuggestionShown}
      onAIStyleClick={props.onAIStyleClick}
      promptItems={props.promptItems}
      promptItemSelected={props.promptItemSelected}
      onPromptSelect={props.onPromptSelect}
      onLoadAiSuggestionClick={props.onLoadSuggestion}
      onRemoveSuggestion={props.onRemoveSuggestion}
      loading={stateProp === 'loading'}
    ></AiContainer>
    {#if !props.aiSuggestions?.length && stateProp !== 'loading'}
      <InfoCard shortText={props.instructionText ?? ''} dimension="blue"></InfoCard>
    {/if}
  {:else if stateProp === 'noOptimization'}
    <InfoCard shortText={props.noOptimizationText ?? ''} dimension="task"></InfoCard>
  {/if}
</BaseCard>
